/**
 * 로그인 — 처음 온 사람에게는 표지.
 *
 * 친구를 초대해 같이 고치려면 "누가 고쳤는지"가 있어야 하고, 그러려면 계정이
 * 필요하다. 그 이상은 받지 않는다 — 닉네임 하나로 쓸 수 있게 한다.
 *
 * 링크만 받고 온 사람은 이 앱을 처음 본다. 로그인 버튼만 있으면 무엇을 하는 앱인지 모른 채
 * 계정을 내놓으라는 셈이다. 버튼은 첫 화면에 그대로 두고(초대받은 사람은 바로 누른다),
 * 그 아래에 할 수 있는 일 세 가지와 실제 화면 하나를 둔다. 더 자세한 건 사용법(#/guide).
 */

import { guideShot } from '@/assets/guide';
import { hasBackend, type SignInMethod } from '@/auth';
import { AppleIcon, GoogleIcon } from '@/components/icons';
import { versionLabel } from '@/config';
import { Rich } from '@/i18n/Rich';
import { useLocale, useT } from '@/i18n';
import { useAuthStore } from '@/store/authStore';

/** 표지의 세 가지 — 문구는 i18n signIn.features와 같은 순서 */
const FEATURE_ICONS = ['👥', '🗺️', '💸'];

function MethodIcon({ method }: { method: SignInMethod }) {
  if (method === 'google') return <GoogleIcon />;
  if (method === 'apple') return <AppleIcon />;
  return null;
}

interface Props {
  methods: readonly SignInMethod[];
  /** 초대 링크로 들어왔는지. 로그인하면 바로 그 여행에 들어간다고 알려준다. */
  invited?: boolean;
}

export function SignInScreen({ methods, invited = false }: Props) {
  const signIn = useAuthStore((s) => s.signIn);
  const error = useAuthStore((s) => s.error);
  const t = useT();
  const locale = useLocale();
  // 사용법 4단계(하루 일정) — 이동 시간이 끼어 있어 이 앱이 무엇인지 한눈에 보인다
  const shot = guideShot(locale, 4);

  return (
    <div className="app">
      <main className="main main--no-tabs signin signin--cover">
        <div className="signin__brand">
          <span className="signin__mark">🧳</span>
          <h1 className="signin__title">TravelTomodachi</h1>
          <p className="signin__sub">{t.signIn.tagline}</p>
        </div>

        {/*
          초대받아 온 사람은 이 앱을 처음 본다. 왜 로그인해야 하는지 모르면
          그냥 닫는다. 로그인하면 바로 그 여행에 들어간다는 걸 먼저 말해준다.
        */}
        {invited && (
          <p className="signin__invited">{t.signIn.invited}</p>
        )}

        <div className="signin__methods">
          {methods.map((method) => (
            <button
              key={method}
              className={`btn${method === 'dev' ? '' : ' btn--primary'}`}
              onClick={() => void signIn(method)}
            >
              <MethodIcon method={method} />
              {t.signIn.method[method]}
            </button>
          ))}
        </div>

        {error && <p className="signin__error">{error}</p>}

        {/* 로그인 전이라 라우터 밖이다 — 주소만 바꾸면 App이 사용법을 띄운다 */}
        <a href="#/guide" className="signin__guide">
          {t.guide.link}
        </a>

        <section className="cover__features" aria-label={t.signIn.tagline}>
          {t.signIn.features.map((f, i) => (
            <div key={f.title} className="cover-feature">
              <span className="cover-feature__icon" aria-hidden>
                {FEATURE_ICONS[i]}
              </span>
              <div>
                <h2 className="cover-feature__title">{f.title}</h2>
                <p className="cover-feature__body">{f.body}</p>
              </div>
            </div>
          ))}
        </section>

        {shot && (
          <img
            className="cover__shot"
            src={shot}
            alt={t.signIn.shotAlt}
            width={360}
            height={740}
            loading="lazy"
            decoding="async"
          />
        )}

        {/*
          이게 진짜 로그인이 아니라는 걸 숨기지 않는다. 진짜처럼 보이면
          친구를 초대했는데 아무 일도 안 일어나는 이유를 못 찾게 된다.
          (개발 서버에서만 온다 — 배포 빌드는 백엔드가 없으면 UnavailableScreen)
        */}
        {!hasBackend && (
          <p className="signin__note">
            <Rich text={t.signIn.devNote} />
          </p>
        )}

        <p className="signin__privacy">
          {t.signIn.privacy}{' '}
          {/* 로그인 전이라 라우터 밖이다 — 주소만 바꾸면 App이 처리방침을 띄운다 */}
          <a href="#/privacy" className="signin__link">
            {t.signIn.privacyLink}
          </a>
        </p>

        <p className="signin__version">{versionLabel(t.common.beta)}</p>
      </main>
    </div>
  );
}
